import { Injectable, Injector } from '@angular/core';
import { Tenant } from '@shared/models/tenant';
import { SignAvvComponent } from '@modules/bcm/@work-flows/sign-avv/sign-avv.component';
import { AvvNoticeComponent } from '@modules/bcm/@work-flows/avv-notice/avv-notice.component';
import { MatDialog } from '@angular/material/dialog';
import { NewBcmComponent } from '@modules/bcm/@work-flows/new-bcm/new-bcm.component';
import { ReadPrivacyAndConditionsComponent } from '@modules/bcm/@work-flows/read-privacy-and-conditions/read-privacy-and-conditions.component';
import { Observable, from, firstValueFrom } from 'rxjs';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { UserService } from '@core/services/user.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { GetProductDialogComponent } from '@sharedComponents/dialogs/get-product-dialog/get-product-dialog.component';
import { Product } from '@shared/models/product';
import { ELECTRIC_METER_PRODUCT_CATEGORY_ID } from '@shared/constants/electric-meter-product-category';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmSettingsSectionName } from '@shared/models/bcm-settings';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { ContractCreatorComponent } from '@bcm-work-flows/contract-creator/contract-creator.component';
import { ContractCreatorMissingDataDialogComponent } from '@bcm-work-flows/contract-creator/contract-creator-missing-data-dialog.component';
import {
    computeContractPreflightMissingFields,
    hasAnyContractMissingFields,
} from '@bcm-work-flows/contract-creator/contract-creator-preflight.util';
import { BcmContractsStoreService } from '@bcmServices/contracts/bcm-contracts-store.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { Boat } from '@shared/models/boat';
import { Berth } from '@shared/models/berth';
import { BcmBooking } from '@shared/models/bcm-booking';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';
import { BcmWorkflowApiService } from '@modules/bcm/@shared/services';
import { BerthBoatAssignment, NewBerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { AssignUserComponent } from '@bcm-work-flows/docksite-order/assign-user/assign-user.component';
import { BcmOrder } from '@shared/models/bcm-order';
import { InformationDialogService } from '@shared/components/dialogs/information-dialog/information-dialog.service';
import { BcmUser } from '../bcm-user';
import { NewCustomerComponent } from '@bcm-work-flows/new-customer/new-customer.component';
import { TranslationService } from '@core/translation/translation.service';
import { startOfToday } from 'date-fns';
import { endOfToday } from '@core/date.facade';
import { BerthsMapFilterService, DateRange } from '@bcmServices/berths/berths-map-filter.service';
import { BookingDialogComponent } from '@sharedComponents/dialogs/booking-dialog/booking-dialog.component';
import { BerthReservationComponent } from '@bcm-work-flows/berth-reservation/berth-reservation.component';
import { U2bValidators } from '@shared/validators/validators';
import { MapObjectBerth } from '@shared/models/map-object-berth';
import { GetFromToRangeDialogComponent } from '@sharedComponents/dialogs/get-from-to-range-dialog/get-from-to-range-dialog.component';

@Injectable({providedIn: 'root'})
export class WorkFlowService {

    private _isOpeningContractCreator = false;

    constructor(private _matDialog: MatDialog,
                private _userService: UserService,
                private _confirmDialogService: ConfirmDialogService,
                private _appNotificationService: AppNotificationService,
                private _informationDialogService: InformationDialogService,
                private _bcmWorkflowApiService: BcmWorkflowApiService,
                private bcmSettingsFacade: BcmSettingsFacade,
                private _berthsFilterService: BerthsMapFilterService,
                private _bcmNavigationService: BcmNavigationService,
                private _injector: Injector,
                private _bcmService: BcmService) {
    }

    async addBerthAssignment(berths: Berth[], bookingType: BookingType, dateRange?: DateRange) {
        if (berths.length > 1) {
            const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent);
            dialogRef.componentInstance.fromFieldOptional = false;
            dialogRef.componentInstance.toFieldOptional = true;
            dialogRef.componentInstance.types = [];
            if (dateRange) {
                dialogRef.componentInstance.fromDateDefault = dateRange.start;
                dialogRef.componentInstance.toDateDefault = dateRange.end;
            }

            const range = await dialogRef.afterClosed().toPromise();

            if (range === false) {
                return;
            }

            return this.newBerthReservation(berths, bookingType, range.from, range.to);
        } else {
            return this.newBerthReservation([berths[0]], bookingType);
        }
    }



    newBerthReservation(berths?: Berth[], bookingType: BookingType = BookingType.Reservation, startDate?: Date, endDate?: Date): Observable<NewBerthBoatAssignment | undefined> {

        const lastFilterData = this._berthsFilterService.lastBerthFilters[0];
        const boat = lastFilterData?.boat;
        const from = startDate ?? (lastFilterData?.start || startOfToday());
        const to = endDate ?? (lastFilterData?.end);

        const matDialogRef = this._matDialog.open(
            BookingDialogComponent,
            {
                disableClose: true,
                panelClass: ['default-dialog', 'xxl-dialog'],
                data: {
                    preselect: {
                        berths,
                        boat,
                        from,
                        to,
                    },
                    bookingType
                }
            }
        );

        return matDialogRef.afterClosed();
    }

    switchToBooking(assignment: BerthBoatAssignment): Observable<NewBerthBoatAssignment | undefined> {
        const matDialogRef = this._matDialog.open(
            BerthReservationComponent,
            {data: {assignment, berthAssignmentType: BookingType.Booking}}
        );

        return matDialogRef.afterClosed();
    }

    newDocksiteOrder(): Observable<boolean> {
        return from(this._bcmNavigationService.navigate(['orders', 'new']).then(() => true));
    }

    assignUserToOrder(order: BcmOrder, _users?: BcmUser[]): Observable<BcmOrder> {
        const matDialogRef = this._matDialog.open(AssignUserComponent, {
            data: {
                order,
            },
        });

        return matDialogRef.afterClosed();
    }

    cancelBerthReservation(assignment: BerthBoatAssignment, doneFn: () => void): void {
        const typeLabel = assignment.type === BookingType.Reservation
            ? TranslationService.translate('bcmBerths.bookingTypes.reservation')
            : TranslationService.translate('bcmBerths.bookingTypes.booking');
        this._confirmDialogService
            .useWarnTheme()
            .setBody(
                TranslationService.translate('bcmBerths.dialogs.cancelBerthAssignmentBody', { type: typeLabel })
            )
            .appendInputToBody({
                name: 'cancelInvoice',
                type: 'checkbox',
                label: TranslationService.translate('common.cancelInvoiceIfExists'),
                defaultValue: true
            })
            .appendInputToBody({
                name: 'removeInvoicePositions',
                type: 'checkbox',
                label: TranslationService.translate('common.deletePositionsIfExists'),
                defaultValue: true
            })
            .openAndReturnResult<{ cancelInvoice: boolean, removeInvoicePositions: boolean }>()
            .subscribe(result => {
                if (!result) {
                    return;
                }

                this._bcmWorkflowApiService
                    .berthReservationCancellation({
                        assignment,
                        cancelInvoice: result.cancelInvoice,
                        removeInvoicePositions: result.removeInvoicePositions
                    })
                    .subscribe((response: { result: boolean, errors: Error[] }) => {
                        this._appNotificationService.showSuccess('Reservierung storniert.');

                        if (response.errors?.length) {
                            this._informationDialogService
                                .setBody(
                                    '<p>Die Stornierung war teilweise erfolgreich.</p>' +
                                    '<p>Folgende Probleme sind aufgetreten:</p>' +
                                    response.errors.map(error => `<p>${error.message}</p>`)
                                );
                        }

                        if (typeof doneFn === 'function') {
                            doneFn();
                        }
                    });
            });
    }

    signAVV(): Promise<any> {
        const confirmDialogRef = this._matDialog.open(SignAvvComponent);

        return new Promise((resolve, reject) => {
            confirmDialogRef
                .afterClosed()
                .subscribe(resolve, reject);
        });
    }

    showAvvNotice(): Promise<any> {
        const dialogRef = this._matDialog.open(AvvNoticeComponent);

        return new Promise((resolve, reject) => {
            dialogRef
                .afterClosed()
                .subscribe(resolve, reject);
        });
    }

    showConditionsDialog(): void {
        this._matDialog.open(ReadPrivacyAndConditionsComponent);
    }

    showConditionsNotice(): void {
        this._informationDialogService
            .setTitle(TranslationService.translate('bcmErrors.conditionsNotice.title'))
            .setBody(TranslationService.translate('bcmErrors.conditionsNotice.body'))
            .open();
    }

    newBcm(): Promise<Tenant> {
        const confirmDialogRef = this._matDialog.open(NewBcmComponent, {disableClose: true});

        return new Promise((resolve, reject) => {
            confirmDialogRef
                .afterClosed()
                .subscribe(resolve, reject);
        });
    }

    newCustomer(): Observable<Tenant> {
        return this._matDialog.open(NewCustomerComponent, {disableClose: true}).afterClosed();
    }

    async checkElectricMeterProduct(isUpdate = false): Promise<any> {
        const {user} = this._userService;
        const settings = await this.bcmSettingsFacade
            .loadSettings()
            .toPromise();

        const productID = settings[BcmSettingsSectionName.ElectricMeter]?.usageProductId;

        if (!isUpdate && productID != null && productID > 0) {
            return;
        }

        return new Promise<boolean>((resolve) => {
            this._confirmDialogService
                .setDialogConfig({
                    disableClose: true
                })
                .setTheme('accent')
                .setTitle(isUpdate ? 'Stromgebühr ändern' : 'Stromgebühr festlegen')
                .setNoButton({text: isUpdate ? TranslationService.translate('common.cancel') : TranslationService.translate('workflows.screens.signAvv.notNow')})
                .setYesButton({text: isUpdate ? 'Weiter...' : 'Stromgebühr festlegen!'})
                .setBody(
                    `<h2>Hallo ${user.firstName},</h2>` +
                    (isUpdate
                        ? ''
                        : `<p>
                               für das Modul Stromanschlüsse muss eine Stromgebühr hinterlegt werden, welche für die 
                               Berechnung der Stromkosten bei Ablesungen der Stromanschluss verwendet wird.
                           </p>`) +
                    `<p>
                            Bitte wähle im nächsten Schritt ein Produkt der Warengruppe <strong>Stromgebühr</strong> aus der 
                            Liste und speichere diese Einstellung.
                        </p>`
                )
                .appendSelectToBody<any>({
                    name: 'productType',
                    type: 'select',
                    label: 'Produkttyp wählen',
                    list: [
                        {
                            key: 'usageProductId',
                            description: 'Standardprodukt für die Stromabrechnung nach Verbrauch (Preis pro kWh/Stunde)'
                        },
                        {
                            key: 'prepaidProductId',
                            description: 'Standardprodukt für die pre-paid Stromabrechnung'
                        },
                        {
                            key: 'flatrateProductId',
                            description: 'Standardprodukt für die pauschale Stromabrechnung'
                        }],
                    listAttribute: 'description',
                    listAttributeTranslation: 'description',
                    validators: [U2bValidators.required(TranslationService.translate('common.validationMsg.makeSelection'))],
                })
                .openAndReturnResult<any>()
                .subscribe(async (result) => {
                    const updatedSettingKey = result?.productType?.key;
                    if (!updatedSettingKey) {
                        this._appNotificationService.showError(TranslationService.translate('workflows.electricMeter.productCategoryResolveFailed'));
                        return;
                    }

                    const dialogRef = this._matDialog.open(GetProductDialogComponent, {
                        data: {
                            categoryIds: [ELECTRIC_METER_PRODUCT_CATEGORY_ID],
                            showQuantityField: false
                        }
                    });
                    const {product}: { product: Product, quantity: number } = await dialogRef.afterClosed().toPromise();

                    if (product instanceof Product) {
                        this.bcmSettingsFacade
                            .updateSection(BcmSettingsSectionName.ElectricMeter, {
                                [updatedSettingKey]: product.id
                            })
                            .add(() => resolve(true));
                    }

                    resolve(false);
                });

            /*
                .openWithCallback(async () => {
                    const dialogRef = this._matDialog.open(GetProductDialogComponent, {
                        data: {
                            categoryIds: [ELECTRIC_METER_PRODUCT_CATEGORY_ID],
                            showQuantityField: false
                        }
                    });
                    const {product}: { product: Product, quantity: number } = await dialogRef.afterClosed().toPromise();

                    if (product instanceof Product) {
                        this.bcmSettingsFacade
                            .updateSection(BcmSettingsSectionName.ElectricMeter, {
                                usageProductId: product.id
                            })
                            .add(() => resolve(true));
                    }

                    resolve(false);
                }, () => resolve(isUpdate));*/
        });
    }

    createContractForPerson(person: Person, boat?: Boat): Observable<any> {
        return from(this._openContractCreatorWithPreflight({ person, boat }));
    }

    createContractForCompany(company: Company, boat?: Boat): Observable<any> {
        return from(this._openContractCreatorWithPreflight({ company, boat }));
    }

    createContractForBooking(booking: BcmBooking): Observable<any> {
        return from(this._openContractCreatorWithPreflight({
            booking,
            fromDate: booking.from,
            toDate: booking.to,
        }));
    }

    private async _openContractCreatorWithPreflight(data: {
        booking?: BcmBooking;
        person?: Person;
        company?: Company;
        boat?: Boat;
        fromDate?: Date;
        toDate?: Date;
    }): Promise<unknown> {
        // Awaiting the templates leaves a gap without a dialog backdrop - ignore repeated clicks. (#7052)
        if (this._isOpeningContractCreator) {
            return false;
        }

        this._isOpeningContractCreator = true;

        try {
            // Resolved lazily: the store loads all contracts on creation, which 403s at app start for users without bcm_contracts_r. (#7052)
            const contractsStore = this._injector.get(BcmContractsStoreService);
            await contractsStore.fetchAll();

            // The wizard takes person, company and boat from the booking when it has one.
            const { booking } = data;
            const person = booking ? booking.person : data.person;

            const missingFields = computeContractPreflightMissingFields({
                tenant: this._bcmService.tenant,
                contracts: contractsStore.contracts ?? [],
                booking,
                person,
                company: booking ? booking.company : data.company,
                boat: booking ? booking.boat : data.boat,
            });

            if (missingFields && hasAnyContractMissingFields(missingFields)) {
                const proceed = await firstValueFrom(this._matDialog.open(ContractCreatorMissingDataDialogComponent, {
                    data: {
                        missingFields,
                        isPerson: !!person,
                    },
                    panelClass: ['default-dialog'],
                }).afterClosed());

                if (!proceed) {
                    return false;
                }
            }

            return await firstValueFrom(this._matDialog.open(ContractCreatorComponent, {
                data,
                panelClass: ['xxl-dialog'],
            }).afterClosed());
        } finally {
            this._isOpeningContractCreator = false;
        }
    }
}
